import { useEffect, useMemo, useState, type JSX } from 'react'
import { MessageSquare, Copy, Check } from 'lucide-react'
import { Button } from 'primereact/button'
import { api } from '../../api/client.js'
import { useAuthStore } from '../../store/authStore.js'
import { useToast } from '../../components/system/ToastProvider.js'
import PageHeader from '../../components/admin/PageHeader.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { FormContainer, FormGrid, FormField } from '../../components/forms/layout/index.js'
import { FormInput, FormSelect, FormTextarea } from '../../components/forms/layout/FormInput.js'
import type { PortalChatWidgetConfig, PortalConfiguration } from '@kc3/contracts'

const DEFAULT_WIDGET: PortalChatWidgetConfig = {
  enabled: false,
  allowedDomains: [],
  position: 'bottom_right',
  offsetBottom: 24,
  offsetRight: 24,
  iconSize: 56,
  primaryColor: '#3454cf',
  welcomeMessage: 'Hi there! How can we help you today?',
  showUnreadCount: true,
}

const POSITION_OPTIONS = [
  { label: 'Bottom right', value: 'bottom_right' },
  { label: 'Bottom left', value: 'bottom_left' },
]

export default function ChatWidgetPage(): JSX.Element {
  const toast = useToast()
  const tenantSlug = useAuthStore((s) => s.user?.tenantSlug ?? '')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [copied, setCopied] = useState(false)
  const [config, setConfig] = useState<PortalChatWidgetConfig>(DEFAULT_WIDGET)
  const [draftConfig, setDraftConfig] = useState<PortalChatWidgetConfig>(DEFAULT_WIDGET)
  const [scriptBaseUrl, setScriptBaseUrl] = useState('')
  const [lastPublished, setLastPublished] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setScriptBaseUrl(window.location.origin)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .get<PortalConfiguration>('/admin/portal-config')
      .then((res) => {
        if (cancelled) return
        const draft = res.draftConfig.chatWidget ?? DEFAULT_WIDGET
        const published = res.publishedConfig.chatWidget ?? DEFAULT_WIDGET
        setDraftConfig(draft)
        setConfig(published)
        setLastPublished(res.publishedAt)
      })
      .catch((err) => {
        if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load widget config')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [toast])

  const embedCode = useMemo(() => {
    const base = scriptBaseUrl || 'https://<your-domain>'
    return `<script
  src="${base}/widget/v1/chat.js"
  data-tenant-id="${tenantSlug}"
  async>
</script>`
  }, [scriptBaseUrl, tenantSlug])

  function update<K extends keyof PortalChatWidgetConfig>(key: K, value: PortalChatWidgetConfig[K]) {
    setDraftConfig((prev) => ({ ...prev, [key]: value }))
  }

  async function saveDraft() {
    setSaving(true)
    try {
      const res = await api.patch<PortalConfiguration>('/admin/portal-config/draft', {
        section: 'chatWidget',
        values: draftConfig,
      })
      toast.success('Draft saved')
      setConfig(res.publishedConfig.chatWidget ?? DEFAULT_WIDGET)
      setLastPublished(res.publishedAt)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save draft')
    } finally {
      setSaving(false)
    }
  }

  async function publish() {
    setPublishing(true)
    try {
      const res = await api.post<PortalConfiguration>('/admin/portal-config/publish')
      toast.success('Widget settings published')
      const published = res.publishedConfig.chatWidget ?? DEFAULT_WIDGET
      setConfig(published)
      setDraftConfig(published)
      setLastPublished(res.publishedAt)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to publish')
    } finally {
      setPublishing(false)
    }
  }

  function copyEmbedCode() {
    void navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true)
      toast.success('Embed code copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function parseDomains(value: string): string[] {
    return value
      .split('\n')
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean)
  }

  if (loading) {
    return (
      <RequirePermission permission="admin:portal:manage">
        <div className="p-6 text-sm text-gray-500">Loading…</div>
      </RequirePermission>
    )
  }

  const isDirty = JSON.stringify(draftConfig) !== JSON.stringify(config)

  return (
    <RequirePermission permission="admin:portal:manage">
      <div className="space-y-6 p-6">
        <PageHeader
          title="Chat Widget / Embed Code"
          description="Configure the floating chat widget customers can embed on any website."
          actions={
            <div className="flex gap-2">
              <Button
                label={saving ? 'Saving…' : 'Save Draft'}
                onClick={() => void saveDraft()}
                loading={saving}
                disabled={!isDirty}
              />
              <Button
                label={publishing ? 'Publishing…' : 'Publish'}
                onClick={() => void publish()}
                loading={publishing}
                className="bg-[var(--kc3-accent)] text-white"
              />
            </div>
          }
        />

        <FormContainer>
          <FormGrid columns={2}>
            <FormSelect
              id="position"
              label="Position"
              value={draftConfig.position}
              options={POSITION_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => update('position', e.value as PortalChatWidgetConfig['position'])}
            />
            <FormInput
              id="primaryColor"
              label="Primary color"
              value={draftConfig.primaryColor}
              onChange={(e) => update('primaryColor', e.currentTarget.value)}
            />
            <FormInput
              id="offsetBottom"
              label="Offset from bottom (px)"
              type="number"
              value={String(draftConfig.offsetBottom)}
              onChange={(e) => update('offsetBottom', Number(e.currentTarget.value))}
            />
            <FormInput
              id="offsetRight"
              label="Offset from side (px)"
              type="number"
              value={String(draftConfig.offsetRight)}
              onChange={(e) => update('offsetRight', Number(e.currentTarget.value))}
            />
            <FormInput
              id="iconSize"
              label="Icon size (px)"
              type="number"
              value={String(draftConfig.iconSize)}
              onChange={(e) => update('iconSize', Number(e.currentTarget.value))}
            />
          </FormGrid>

          <FormTextarea
            id="welcomeMessage"
            label="Welcome message"
            rows={2}
            value={draftConfig.welcomeMessage}
            onChange={(e) => update('welcomeMessage', e.currentTarget.value)}
            className="mt-4"
          />

          <FormTextarea
            id="allowedDomains"
            label="Allowed domains (one per line)"
            help="Leave blank to allow any domain. Use *.example.com to allow all subdomains."
            rows={4}
            value={draftConfig.allowedDomains.join('\n')}
            onChange={(e) => update('allowedDomains', parseDomains(e.currentTarget.value))}
            className="mt-4"
          />

          <FormField label="Enable widget" className="mt-4" help="When disabled, the embed script will return an error for all customer sites.">
            <input
              id="enabled"
              type="checkbox"
              checked={draftConfig.enabled}
              onChange={(e) => update('enabled', e.currentTarget.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[var(--kc3-accent)] focus:ring-[var(--kc3-accent)]"
            />
          </FormField>

          <FormField label="Show unread count" className="mt-4">
            <input
              id="showUnreadCount"
              type="checkbox"
              checked={draftConfig.showUnreadCount}
              onChange={(e) => update('showUnreadCount', e.currentTarget.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[var(--kc3-accent)] focus:ring-[var(--kc3-accent)]"
            />
          </FormField>
        </FormContainer>

        <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
          <h3 className="text-base font-semibold text-gray-900">Embed code</h3>
          <p className="text-sm text-gray-500">
            Add this single <code>&lt;script&gt;</code> tag to any page where you want the widget to appear.
          </p>

          <FormField label="Widget script base URL" help="The API/public host that serves the chat script. Update if your API is on a different host than this admin app.">
            <FormInput
              id="scriptBaseUrl"
              value={scriptBaseUrl}
              onChange={(e) => setScriptBaseUrl(e.currentTarget.value)}
            />
          </FormField>

          <div className="relative">
            <pre className="rounded-md bg-gray-50 p-4 text-sm text-gray-800 overflow-x-auto border border-gray-200">{embedCode}</pre>
            <button
              type="button"
              onClick={copyEmbedCode}
              className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm border border-gray-200 hover:bg-gray-50"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          {lastPublished && (
            <p className="text-xs text-gray-400">Last published: {new Date(lastPublished).toLocaleString()}</p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Preview</h3>
          <div className="relative h-80 w-full rounded-md border border-dashed border-gray-300 bg-gray-50 overflow-hidden">
            <div
              className="absolute flex items-center justify-center rounded-full text-white shadow-lg"
              style={{
                width: `${draftConfig.iconSize}px`,
                height: `${draftConfig.iconSize}px`,
                backgroundColor: draftConfig.primaryColor,
                bottom: `${draftConfig.offsetBottom}px`,
                [draftConfig.position === 'bottom_right' ? 'right' : 'left']: `${draftConfig.offsetRight}px`,
              }}
              aria-label="Chat widget preview"
            >
              <MessageSquare className="h-6 w-6" />
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-gray-400">
              The real widget opens the live chat interface when clicked.
            </div>
          </div>
        </div>
      </div>
    </RequirePermission>
  )
}
